import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, User } from 'lucide-react';
import { Button } from './ui/button';
import { useCustomerAuth } from '../contexts/CustomerAuthContext';

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { customer, isAuthenticated } = useCustomerAuth();

  const navigation = [
    { name: 'Home', href: '/' },
    { name: 'Services', href: '/services' },
    { name: 'Fleet', href: '/fleet' },
    { name: 'About', href: '/about' },
    { name: 'Contact', href: '/contact' }
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-200 shadow-sm">
      <nav className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-[110px]">
          {/* Logo */}
          <Link to="/" className="flex-shrink-0">
            <img
              src="https://customer-assets.emergentagent.com/job_luxury-ride-9/artifacts/bzsgrjri_Black%4010x.png"
              alt="Obsidian Chauffeurs"
              className="h-[96px] w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex lg:items-center lg:space-x-8">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`text-sm font-medium tracking-wide transition-all duration-300 relative group ${
                  isActive(item.href) ? 'text-black' : 'text-gray-600 hover:text-black'
                }`}
              >
                {item.name}
                <span
                  className={`absolute -bottom-1 left-0 w-full h-0.5 bg-black transition-transform duration-300 ${
                    isActive(item.href) ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'
                  }`}
                />
              </Link>
            ))}
          </div>

          {/* Contact Info & CTA */}
          <div className="hidden lg:flex lg:items-center lg:space-x-5">
            <a href="tel:03333357733" className="text-gray-600 hover:text-black transition-colors duration-300 flex items-center space-x-2">
              <Phone className="h-4 w-4" />
              <span className="text-sm font-medium">0333 335 7733</span>
            </a>
            {isAuthenticated ? (
              <Link to="/my-trips" className="flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-black" data-testid="header-account-link">
                <span className="h-8 w-8 rounded-full bg-black text-white flex items-center justify-center text-xs font-bold">
                  {(customer.firstName?.[0] || '?').toUpperCase()}
                </span>
                <span>My Trips</span>
              </Link>
            ) : (
              <Link to="/login" className="text-sm font-medium text-gray-600 hover:text-black flex items-center gap-1.5" data-testid="header-login-link">
                <User className="h-4 w-4" />
                Sign In
              </Link>
            )}
            <Link to="/booking">
              <Button className="bg-black text-white hover:bg-gray-800 font-medium px-6">
                Book Now
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            type="button"
            className="lg:hidden text-black p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </nav>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden bg-white border-t border-gray-200">
          <div className="px-6 py-4 space-y-3">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`block py-2 text-base font-medium ${
                  isActive(item.href) ? 'text-black' : 'text-gray-600'
                }`}
                onClick={() => setMobileMenuOpen(false)}
              >
                {item.name}
              </Link>
            ))}
            <div className="pt-4 space-y-3 border-t border-gray-200">
              <a href="tel:03333357733" className="flex items-center space-x-2 text-gray-600">
                <Phone className="h-4 w-4" />
                <span className="text-sm">0333 335 7733</span>
              </a>
              {isAuthenticated ? (
                <Link to="/my-trips" onClick={() => setMobileMenuOpen(false)} className="block py-2 text-base font-medium text-gray-700">
                  My Trips
                </Link>
              ) : (
                <Link to="/login" onClick={() => setMobileMenuOpen(false)} className="block py-2 text-base font-medium text-gray-700">
                  Sign In / Create Account
                </Link>
              )}
              <Link to="/booking" onClick={() => setMobileMenuOpen(false)}>
                <Button className="w-full bg-black text-white hover:bg-gray-800">
                  Book Now
                </Button>
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
